import {
    AfterViewChecked,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject, Observable, ReplaySubject, Subject, combineLatest, of } from 'rxjs';
import {
    delay,
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    startWith,
    switchMap,
    take,
    takeUntil
} from 'rxjs/operators';

import {
    ActionItem,
    CheckboxSelectionFormValues,
    ColumnDef as SortableColumnDef,
    FieldDefinition,
    FormValueState,
    QueryPartData,
    ShColumnFilterField,
    ShFacet,
    ShSelectedFacetValue,
    SortDirection,
    SortInfo
} from '@stylehaven/components';
import { Column } from '../../entities/column.entity';
import { Comparable } from '../../entities/misc/comparable.interface';
import { Trackable } from '../../entities/misc/trackable.interface';
import {
    ColumnFilterFieldsCache,
    getColumnEnumFacet,
    getColumnFilterValue,
    getColumnSelectedEnumValues,
    hasColumnFilter,
    isColumnDateFilterable,
    isColumnFacetFilterable,
    isColumnTextFilterable,
    toSortColumnDef
} from '../../helpers/column-header-controls.helper';

export const CARD_TABLE_SCROLL_REF = '.dgh-overview-page .digihaven-card-table-scroller';

function isBatchSelected(value: CheckboxSelectionFormValues | null): boolean {
    return value?.selection.find(item => item.value === 'batch')?.state === FormValueState.SET;
}

@Component({
    standalone: false,
    selector: 'digihaven-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss']
})
export class ListComponent<T extends Comparable & Trackable> implements OnInit, OnDestroy, AfterViewChecked {
    readonly destroy$: Subject<void> = new Subject<void>();
    readonly records$: BehaviorSubject<Observable<T>[]> = new BehaviorSubject<Observable<T>[]>([]);
    readonly rowActionItems$: ReplaySubject<Observable<ActionItem>[]> = new ReplaySubject(1);
    readonly actionBarActionItems$: ReplaySubject<Observable<ActionItem>[]> = new ReplaySubject(1);
    readonly columns$: ReplaySubject<Column<T>[]> = new ReplaySubject(1);
    readonly enableSelect$: ReplaySubject<boolean> = new ReplaySubject(1);
    readonly resetSelection$: Subject<void> = new Subject<void>();

    @Input()
    set records(records: Observable<T>[]) {
        this.records$.next(records || []);
    }

    @Input()
    set enableSelect(enableSelect: boolean) {
        this.enableSelect$.next(enableSelect);
    }

    @Input()
    set columns(columns: Column<T>[]) {
        this.columns$.next(columns);
    }

    @Input()
    set rowActionItems(rowActionItems: Observable<ActionItem>[]) {
        this.rowActionItems$.next(rowActionItems);
    }

    @Input()
    set actionBarActionItems(items: Observable<ActionItem>[]) {
        if (items) {
            this.actionBarActionItems$.next(items);
        }
    }

    @Input()
    set resetSelection(resetSelection: void) {
        this.resetSelection$.next();
    }

    @Input()
    fieldDefinitions!: FieldDefinition[];

    @Input()
    isLoading!: boolean;

    @Input()
    hasError!: boolean;

    @Input()
    condensed = false;

    @Input()
    overviewId?: string;

    @Input()
    totalNumberOfResults?: number;

    @Input()
    batchActionItems?: ActionItem[];

    @Input()
    currentSort: SortInfo = { field: '', direction: SortDirection.ASC };

    @Input()
    activeColumnFilters: QueryPartData[] = [];

    @Input()
    activeFacetFilters: QueryPartData[] = [];

    @Input()
    enumFacets: ReadonlyMap<string, ShFacet> | null = null;

    @Input()
    enumFacetFieldKeys: string[] = [];

    @Output()
    rowNavigation: EventEmitter<T> = new EventEmitter<T>();

    @Output()
    sortChanged: EventEmitter<SortInfo> = new EventEmitter<SortInfo>();

    @Output()
    columnFilterChanged: EventEmitter<{ field: string; value: string }> = new EventEmitter<{
        field: string;
        value: string;
    }>();

    @Output()
    enumFilterOpened: EventEmitter<string> = new EventEmitter<string>();

    @Output()
    facetValueSelected: EventEmitter<ShSelectedFacetValue> = new EventEmitter<ShSelectedFacetValue>();

    @Output()
    enumFilterCleared: EventEmitter<string> = new EventEmitter<string>();

    @Output()
    selectionChanged: BehaviorSubject<Observable<T>[]> = new BehaviorSubject<Observable<T>[]>([]);

    @Output()
    recordInteraction: EventEmitter<T> = new EventEmitter<T>();

    private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

    @ViewChild('tableScroller')
    private readonly tableScroller?: ElementRef<HTMLElement>;

    private resizeObserver?: ResizeObserver;
    private observedElement?: HTMLElement;

    /** Called from the template, so it has to hand back the same array while nothing has changed. */
    private readonly columnFilterFields = new ColumnFilterFieldsCache();

    /**
     * Starts `true` to catch content that already overflows on load; set back to `true` whenever
     * columns/records change (the only content-driven cause of overflow that a resize won't
     * report on its own). Gates the `ngAfterViewChecked` DOM read so it isn't forced on every
     * app-wide change-detection cycle, only on cycles that follow an actual content change.
     */
    private needsScrollableContentCheck = true;

    hasScrollableContent = false;
    hasScrolledFromStart = false;

    readonly selectAllCheckbox = new FormControl<CheckboxSelectionFormValues | null>(null);
    readonly selectPageCheckbox = new FormControl<boolean>(false);
    readonly showBatchActions$ = this.selectAllCheckbox.valueChanges.pipe(
        map(value => isBatchSelected(value)),
        distinctUntilChanged()
    );

    /** Keyed by {@link Trackable.trackBy}, never by row position: nothing clears this when the page changes. */
    private readonly selectedRecordsById: Map<string, Observable<T>> = new Map();

    columnCount$!: Observable<number>;

    readonly selectAllOptions = [
        {
            label: 'Het volledig zoekresultaat selecteren',
            value: 'batch'
        }
    ];

    constructor() {
        this.handleSelectCheckboxValueChange();
        this.setupColumnCountStream();
        this.watchForContentChanges();

        this.selectPageCheckbox.valueChanges.subscribe(selectPageCheckbox => {
            this.selectAllCheckbox.setValue({
                checkbox: {
                    value: selectPageCheckbox,
                    state: selectPageCheckbox ? FormValueState.SET : FormValueState.UNSET
                },
                selection: []
            });
        });
    }

    ngOnInit(): void {
        this.listenForClearSelection();
        this.listenForSelectAll();

        this.showBatchActions$
            .pipe(
                takeUntil(this.destroy$),
                filter(value => value)
            )
            .subscribe(() => this.toggleSelectAll(true));

        this.records$
            .pipe(
                filter(() => this.isBatchSelected()),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.setPageSelected(true);
                this.emitInteractionOnAllRecords();
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();

        this.resizeObserver?.disconnect();
        this.cleanUpStreams();
    }

    /**
     * Also observes real element resizes (e.g. browser/column-width changes) that don't trigger
     * an Angular change-detection cycle on their own — ngAfterViewChecked alone would miss those.
     * The DOM read behind `updateScrollableContent` only runs when `needsScrollableContentCheck`
     * is set, so this hook stays cheap on the (far more frequent) cycles where columns/records
     * haven't changed, instead of forcing a layout read on every app-wide CD cycle.
     */
    ngAfterViewChecked(): void {
        if (!this.tableScroller) {
            return;
        }

        const element: HTMLElement = this.tableScroller.nativeElement;
        if (element !== this.observedElement) {
            this.observeResize(element);
            this.needsScrollableContentCheck = true;
        }

        if (!this.needsScrollableContentCheck) {
            return;
        }

        this.needsScrollableContentCheck = false;
        this.updateScrollableContent(element);
    }

    onRecordMenuSelected(record: T): void {
        this.recordInteraction.emit(record);
    }

    getSortColumnDef(column: Column<T>): SortableColumnDef<any, any> {
        return toSortColumnDef(column);
    }

    onSortInfoChanged(sortInfo: SortInfo): void {
        this.sortChanged.emit(sortInfo);
    }

    isColumnFilterable(column: Column<T>): boolean {
        return isColumnTextFilterable(column);
    }

    /** Whether this column shows a filter control at all, in any of its three variants. */
    hasColumnFilter(column: Column<T>): boolean {
        return hasColumnFilter(column, this.enumFacetFieldKeys);
    }

    isColumnDateFilterable(column: Column<T>): boolean {
        return isColumnDateFilterable(column);
    }

    isColumnFacetFilterable(column: Column<T>): boolean {
        return isColumnFacetFilterable(column, this.enumFacetFieldKeys);
    }

    getColumnFilterValue(column: Column<T>): string {
        return getColumnFilterValue(column, this.activeColumnFilters);
    }

    getColumnFilterFields(column: Column<T>): ShColumnFilterField[] {
        return this.columnFilterFields.get(column, this.activeColumnFilters);
    }

    getEnumFacet(column: Column<T>): ShFacet | null {
        return getColumnEnumFacet(column, this.enumFacets);
    }

    getSelectedEnumValues(column: Column<T>): string[] {
        return getColumnSelectedEnumValues(column, this.activeFacetFilters);
    }

    onColumnFilterChanged(field: string, value: string): void {
        this.columnFilterChanged.emit({ field, value });
    }

    onEnumFilterOpened(column: Column<T>): void {
        const dottedKey: string | undefined = column.fieldDefinition?.DottedKey;
        if (dottedKey) {
            this.enumFilterOpened.emit(dottedKey);
        }
    }

    onEnumFilterCleared(column: Column<T>): void {
        const dottedKey: string | undefined = column.fieldDefinition?.DottedKey;
        if (dottedKey) {
            this.enumFilterCleared.emit(dottedKey);
        }
    }

    toggleSelectAll(selectAll: boolean): void {
        this.setSelectAll(selectAll);
    }

    /**
     * Drops "the whole result set" without touching the ticked rows. The checkbox half of the control is
     * left as-is, so the page-selection handler behind `valueChanges` sees no change.
     */
    clearBatchSelection(): void {
        const current: CheckboxSelectionFormValues | null = this.selectAllCheckbox.value;
        const hasBatch: boolean = !!current?.selection.some(item => item.state === FormValueState.SET);

        if (!current || !hasBatch) {
            return;
        }

        this.selectAllCheckbox.setValue({
            checkbox: current.checkbox,
            selection: current.selection.map(item => ({ ...item, state: FormValueState.UNSET }))
        });
    }

    selectRow(record: T, index: number, event: Event): void {
        const clickedElement: HTMLInputElement | null = event.target as HTMLInputElement;
        const isChecked: boolean = clickedElement ? clickedElement.checked : false;
        if (isChecked) {
            this.recordInteraction.emit(record);
            this.selectedRecordsById.set(record.trackBy(), this.records$.value[index]);
        } else {
            this.selectedRecordsById.delete(record.trackBy());
        }
        this.propagateSelectionChanged();
    }

    isRowSelected(record: T): boolean {
        return this.selectedRecordsById.has(record.trackBy());
    }

    recordSelected(record: T): void {
        this.rowNavigation.emit(record);
    }

    onTableScroll(event: Event): void {
        const element = event.target as HTMLElement;
        this.hasScrollableContent = this.computeHasScrollableContent(element);
        this.hasScrolledFromStart = this.computeHasScrolledFromStart(element);
    }

    private observeResize(element: HTMLElement): void {
        this.resizeObserver?.disconnect();
        this.observedElement = element;

        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        this.resizeObserver = new ResizeObserver(() => this.updateScrollableContent(element));
        this.resizeObserver.observe(element);
    }

    /** Only calls `detectChanges()` when a value actually changed, to avoid `ExpressionChangedAfterItHasBeenCheckedError`. */
    private updateScrollableContent(element: HTMLElement): void {
        const hasScrollableContent: boolean = this.computeHasScrollableContent(element);
        const hasScrolledFromStart: boolean = this.computeHasScrolledFromStart(element);
        if (hasScrollableContent !== this.hasScrollableContent || hasScrolledFromStart !== this.hasScrolledFromStart) {
            this.hasScrollableContent = hasScrollableContent;
            this.hasScrolledFromStart = hasScrolledFromStart;
            this.cdr.detectChanges();
        }
    }

    private computeHasScrollableContent(element: HTMLElement): boolean {
        return element.scrollLeft + element.clientWidth < element.scrollWidth - 1;
    }

    private computeHasScrolledFromStart(element: HTMLElement): boolean {
        return element.scrollLeft > 0;
    }

    private isBatchSelected(): boolean {
        return isBatchSelected(this.selectAllCheckbox.value);
    }

    private setSelectAll(areAllValuesSelected: boolean) {
        this.setPageSelected(areAllValuesSelected);

        const oldValue = this.selectAllCheckbox.value;

        if (!oldValue) {
            return;
        }

        this.selectPageCheckbox.setValue(false, { emitEvent: false });
        this.selectAllCheckbox.setValue({
            selection: oldValue.selection.map(value => ({
                ...value,
                state: areAllValuesSelected ? FormValueState.SET : FormValueState.UNSET
            })),
            checkbox: {
                value: areAllValuesSelected,
                state: areAllValuesSelected ? FormValueState.SET : FormValueState.UNSET
            }
        });
    }

    /** Selecting adds this page to what is held; deselecting clears every page's worth. */
    private setPageSelected(pageSelected: boolean): void {
        if (!pageSelected) {
            this.selectedRecordsById.clear();
            this.propagateSelectionChanged();
            return;
        }

        const page: Observable<T>[] = this.records$.value;
        if (page.length === 0) {
            this.propagateSelectionChanged();
            return;
        }

        combineLatest(page)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe((records: T[]) => {
                records.forEach((record, index) => this.selectedRecordsById.set(record.trackBy(), page[index]));
                this.propagateSelectionChanged();
                this.cdr.markForCheck();
            });
    }

    private listenForSelectAll(): void {
        this.selectAllCheckbox.valueChanges
            .pipe(
                startWith(this.selectAllCheckbox.value),
                map(selected => selected?.checkbox?.value!),
                distinctUntilChanged(),
                filter(value => value),
                takeUntil(this.destroy$)
            )
            .subscribe(() => this.emitInteractionOnAllRecords());
    }

    private emitInteractionOnAllRecords(): void {
        this.records$
            .pipe(
                take(1),
                switchMap((records: Observable<T>[]) => combineLatest(records)),
                take(1),
                takeUntil(this.destroy$)
            )
            .subscribe((records: T[]) => records.forEach(record => this.recordInteraction.emit(record)));
    }

    private propagateSelectionChanged(): void {
        this.selectionChanged.next([...this.selectedRecordsById.values()]);
    }

    private listenForClearSelection(): void {
        this.resetSelection$.pipe(takeUntil(this.destroy$)).subscribe(() => this.clearSelection());
    }

    private clearSelection(): void {
        this.toggleSelectAll(false);
    }

    private handleSelectCheckboxValueChange(): void {
        this.selectAllCheckbox.valueChanges
            .pipe(
                // Wait 1 render cycle
                delay(0),
                map(selected => selected?.checkbox.value!),
                distinctUntilChanged()
            )
            .subscribe(allSelected => this.setPageSelected(allSelected));
    }

    private watchForContentChanges(): void {
        combineLatest([this.columns$, this.records$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.needsScrollableContentCheck = true;
            });
    }

    private setupColumnCountStream(): void {
        this.columnCount$ = combineLatest([this.columns$, this.rowActionItems$, this.enableSelect$]).pipe(
            map(([columns, rowActionItems, enableSelect]) => {
                let columnCount: number = columns.length;
                if (rowActionItems) {
                    columnCount++;
                }
                if (enableSelect) {
                    columnCount++;
                }
                return columnCount;
            }),
            shareReplay(1)
        );
    }

    private cleanUpStreams(): void {
        this.records$.complete();
        this.rowActionItems$.complete();
        this.columns$.complete();
        this.enableSelect$.complete();
        this.resetSelection$.complete();
    }

    get batchActionItemObservables() {
        return this.batchActionItems?.map(bla => of(bla));
    }
}
